import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { get as apiGet, post as apiPost } from '../services/api/apiClient';
import MainLayout from '../layouts/MainLayout';
import {
  Card,
  CardPreview,
  CardContent,
  CardTitle,
  CardDescription,
  CardMenu,
  CardBadge,
  ContentSpinner,
  useToast,
} from '../components/ui';

function TrashPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { toast, confirm } = useToast();
  const [trashedProjects, setTrashedProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastFetchTime, setLastFetchTime] = useState(null);

  useEffect(() => {
    loadTrashedProjects();
    cleanupOldProjects();
  }, []);

  const loadTrashedProjects = async (force = false) => {
    const CACHE_DURATION = 30000;
    const now = Date.now();

    if (!force && lastFetchTime && (now - lastFetchTime) < CACHE_DURATION) {
      console.log('📦 Using cached trash projects data (loaded', Math.round((now - lastFetchTime) / 1000), 'seconds ago)');
      return;
    }

    try {
      setIsLoading(true);
      const response = await apiGet('/projects/user-projects?status=trash');
      if (response.success) {
        setTrashedProjects(response.projects);
        setLastFetchTime(now);
        console.log('✅ Trash projects loaded and cached:', response.projects.length, 'projects');
      }
    } catch (error) {
      console.error('Failed to load trashed projects:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const cleanupOldProjects = async () => {
    try {
      await apiPost('/projects/cleanup-old');
      loadTrashedProjects();
    } catch (error) {
      console.error('Failed to cleanup old projects:', error);
    }
  };

  const handleRestore = async (projectId) => {
    try {
      const response = await fetch(`http://localhost:3001/api/projects/${projectId}/restore`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setTrashedProjects(trashedProjects.filter(p => p.id !== projectId));
        toast.success('Project restored successfully');
      } else {
        throw new Error('Failed to restore project');
      }
    } catch (error) {
      console.error('Failed to restore project:', error);
      toast.error('Failed to restore project. Please try again.');
    }
  };

  const handlePermanentDelete = async (projectId, projectName) => {
    const confirmed = await confirm({
      title: 'Permanently Delete Project',
      message: `Are you sure you want to permanently delete "${projectName}"? This action cannot be undone.`,
      confirmText: 'Delete Forever',
      cancelText: 'Cancel',
      variant: 'destructive'
    });

    if (!confirmed) return;

    try {
      const response = await fetch(`http://localhost:3001/api/projects/${projectId}/permanent`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setTrashedProjects(trashedProjects.filter(p => p.id !== projectId));
        toast.success('Project permanently deleted');
      } else {
        throw new Error('Failed to delete project');
      }
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast.error('Failed to delete project. Please try again.');
    }
  };

  const getDaysUntilDeletion = (deletedAt) => {
    if (!deletedAt) return 7;
    const deleted = new Date(deletedAt);
    const now = new Date();
    const diffTime = 7 * 24 * 60 * 60 * 1000 - (now - deleted);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  return (
    <MainLayout activePage="trash">
      <div className="flex-1 flex flex-col">
          {/* Header */}
          <div
            className="py-5 px-10 flex items-center justify-between"
            style={{ borderBottom: `1px solid ${theme.borderColor}` }}
          >
            <div>
              <h1
                className="m-0 mb-2 text-[1.75rem] font-bold"
                style={{ color: theme.textPrimary }}
              >
                Trash
              </h1>
              <p className="m-0 text-sm" style={{ color: theme.textSecondary }}>
                Projects will be permanently deleted after 7 days
              </p>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 p-10 overflow-y-auto">
            {isLoading ? (
              <ContentSpinner />
            ) : trashedProjects.length === 0 ? (
              <div
                className="flex flex-col items-center justify-center min-h-[400px]"
                style={{ color: theme.textSecondary }}
              >
                <Icon icon="mdi:trash-can-outline" className="text-[64px] mb-4 opacity-50" />
                <p className="text-lg m-0">Trash is empty</p>
              </div>
            ) : (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-6">
                {trashedProjects.map(project => (
                  <TrashProjectCard
                    key={project.id}
                    project={project}
                    theme={theme}
                    onRestore={() => handleRestore(project.id)}
                    onPermanentDelete={() => handlePermanentDelete(project.id, project.name)}
                    getDaysUntilDeletion={getDaysUntilDeletion}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
    </MainLayout>
  );
}

function TrashProjectCard({ project, theme, onRestore, onPermanentDelete, getDaysUntilDeletion }) {
  const daysLeft = getDaysUntilDeletion(project.deleted_at);
  const badgeVariant = daysLeft <= 2 ? 'warning' : 'default';

  return (
    <Card variant="default" interactive>
      {/* More Options */}
      <CardMenu
        options={[
          {
            label: 'Restore',
            icon: 'mdi:restore',
            onClick: onRestore
          },
          { divider: true },
          {
            label: 'Delete Forever',
            icon: 'mdi:delete-forever',
            danger: true,
            onClick: onPermanentDelete
          }
        ]}
      />

      {/* Preview Area */}
      <CardPreview className="mb-4">
        <span className="text-5xl font-extralight opacity-50 text-[var(--text-tertiary)]">
          ViralArc
        </span>
      </CardPreview>

      {/* Project Info */}
      <CardContent>
        <CardTitle className="mb-2">{project.name}</CardTitle>
        <CardDescription className="mb-3">
          Deleted: {new Date(project.deleted_at).toLocaleDateString()}
        </CardDescription>

        {/* Warning Badge */}
        <CardBadge
          variant={badgeVariant}
          className={daysLeft <= 2 ? '' : 'bg-amber-100 text-amber-600'}
        >
          {daysLeft === 0 ? 'Deleting today' : `${daysLeft} days left`}
        </CardBadge>
      </CardContent>
    </Card>
  );
}

export default TrashPage;
